import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useAddExistingClientDeliverableMutation,
  useAddNewDeliverableMutation,
  useGetCreateClientDeliverableById,
  useGetExistingClientDeliverables,
  useUpdateClientDeliverableMutation,
} from "../../../../../../api/services/client/clientApis";
import { CreateDeliverableParams } from "../../../../../../api/services/client/models";
import { InformationBadge } from "../../../../../../common/informationBadge/informationBadge";
import {
  FormController,
  MuiBox,
  MuiModal,
  MuiRadioGroup,
} from "../../../../../../components";
import {
  convertFormDataToLocalizeObj,
  convertLocalizeObjToFormValue,
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../../../redux/slices/client/createClientSelector";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import {
  CreateDeliverablesFormKeys,
  CreateDeliverablesFormProps,
  ExistingDeliverableFilterFormProps,
  ExistingDeliverableFormKeys,
  ExistingDeliverablesType,
} from "../../modelsAndSchemas/models";
import { AddDeliverableValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { CreateDeliverableWrapper } from "./CreateDeliverable.style";
import CreateClientExistingDeliverables from "./CreateDeliverableComponents/ExistingDeliverables/ExistingDeliverables";
import NewDeliverable from "./CreateDeliverableComponents/NewDeliverable/NewDeliverable";

import {
  ADD_DELIVERABLE_TYPE,
  AddDeliverableType,
  DELIVERABLE_FRAMEWORK_TYPE,
} from "../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../helpers/constants/siteCoreKeys";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";

interface CreateClientDeliverableProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  getListOfDeliverablesApi: () => void;
  isEditDeliverable?: boolean;
  deliverableId?: string;
}

export const CreateClientDeliverable: FC<CreateClientDeliverableProps> = ({
  show,
  handleCloseModal,
  pageId,
  getListOfDeliverablesApi,
  isEditDeliverable = false,
  deliverableId,
}) => {
  const dispatch = useDispatch();
  const { id: clientId } = useSelector(createClientSelector);
  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );
  const [selectedUnit, setSelectedUnit] = useState<string>("");

  const [showCancel, setShowCancel] = useState(false);

  const [existingClientDeliverablesList, setExistingClientDeliverablesList] =
    useState<ExistingDeliverablesType[]>([]);

  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    ExistingDeliverablesType[]
  >([]);

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const [measurementShouldReset, setMeasurementShouldReset] = useState(true);

  const {
    commonLabels,
    fetchLabel,
    commonValidationMessages,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    data: existingDeliverablesData,
    isLoading: existingDeliverablePending,
    refetchWithParams: refetchExistingDeliverable,
    isError: isExistingDeliverableError,
    isSuccess: isExistingDeliverableSuccess,
  } = useGetExistingClientDeliverables();

  const {
    mutateAsync: addNewDeliverableApi,
    isPending: isAddNewPending,
    isError: isAddNewError,
    isSuccess: isAddNewSuccess,
  } = useAddNewDeliverableMutation();

  const {
    mutateAsync: addExistingDeliverableApi,
    isPending: isAddExistingPending,
    isError: isAddExistingError,
    isSuccess: isAddExistingSuccess,
  } = useAddExistingClientDeliverableMutation();

  const {
    mutateAsync: updateDeliverableApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateClientDeliverableMutation();

  const {
    data: deliverableData,
    isPending: isPendingDeliverable,
    isError: isErrorDeliverable,
    isSuccess: isSuccessDeliverable,
    refetchWithParams: getDeliverableData,
  } = useGetCreateClientDeliverableById();

  useEffect(() => {
    if (isEditDeliverable && deliverableId) {
      getDeliverableData(deliverableId);
    }
  }, [isEditDeliverable]);

  useEffect(() => {
    if (isExistingDeliverableSuccess) {
      resetClientExistingDeliverables();
    }
  }, [existingDeliverablesData]);

  const resetClientExistingDeliverables = () => {
    if (existingDeliverablesData) {
      const myData = [...existingDeliverablesData];

      myData.forEach((data) => {
        data.unitName = fetchValueByKeyOfDropdown(
          activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
          activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnitList,
          data.units,
        );
        data.deliverableTypeName = fetchValueByKeyOfDropdown(
          activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
          activitiesAndDeliverablesSiteCoreKeys.deliverableTypeList,
          data.deliverableType,
        );
        data.measurements =
          data.units === UnitTypesKey.Minutes
            ? Number(
                fetchValueByKeyOfDropdown(
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableMinuteList,
                  data.measurements,
                ),
              )
            : Number(data.measurements);
      });
      setExistingClientDeliverablesList([...myData]);
      setAllUpdatedRows([...myData]);
      // Check if all rows have isSelected as true
      const allSelected = myData.every((row) => row.isSelected);
      setIsAllSelected(allSelected);

      resetExistingFilter();
      resetPagination();
    }
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isAddNewPending ||
          isAddExistingPending ||
          isPendingDeliverable ||
          isUpdatePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isAddNewPending,
    isAddExistingPending,
    isPendingDeliverable,
    isUpdatePending,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: existingDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [existingDeliverablePending]);

  useEffect(() => {
    if (isAddExistingSuccess || isAddNewSuccess || isUpdateSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
            isEditDeliverable && isUpdateSuccess
              ? activitiesAndDeliverablesSiteCoreKeys.deliverableUpdatedMessage
              : activitiesAndDeliverablesSiteCoreKeys.deliverableAddedMessage,
          ),
        }),
      );
      getListOfDeliverablesApi();
      handleCloseModal();
    }
    if (
      isAddNewError ||
      isAddExistingError ||
      isExistingDeliverableError ||
      isErrorDeliverable ||
      isUpdateError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isAddNewSuccess,
    isAddNewError,
    isAddExistingError,
    isAddExistingSuccess,
    isExistingDeliverableError,
    isErrorDeliverable,
    isUpdateError,
    isUpdateSuccess,
  ]);

  const defaultValues = {
    [CreateDeliverablesFormKeys.addDeliverableType]:
      ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType,
    [CreateDeliverablesFormKeys.deliverableType]: "",
    [CreateDeliverablesFormKeys.name]: { [userSelectedLanguage]: "" },
    [CreateDeliverablesFormKeys.units]: "",
    [CreateDeliverablesFormKeys.measurementMinute]: "",
    [CreateDeliverablesFormKeys.measurementOther]: null,
  };

  const {
    handleSubmit: handleSubmitForm,
    control,
    getValues,
    watch,
    setValue,
    formState: { isDirty: isDirtyForm },
    reset,
    clearErrors,
  } = useForm<CreateDeliverablesFormProps>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      AddDeliverableValidationSchema(
        commonValidationMessages,
        userSelectedLanguage,
      ) as any,
    ),
  });

  useEffect(() => {
    if (isSuccessDeliverable && deliverableData) {
      setSelectedUnit(deliverableData.units);
      reset({
        ...defaultValues,
        [CreateDeliverablesFormKeys.name]: convertLocalizeObjToFormValue(
          deliverableData.localizedDeliverableNames,
        ),
        [CreateDeliverablesFormKeys.deliverableType]:
          deliverableData.deliverableType,
        [CreateDeliverablesFormKeys.units]: deliverableData.units,
        ...(deliverableData.units === UnitTypesKey.Minutes
          ? {
              [CreateDeliverablesFormKeys.measurementMinute]:
                deliverableData.measurements,
            }
          : {
              [CreateDeliverablesFormKeys.measurementOther]:
                deliverableData.measurements,
            }),
      });
      setMeasurementShouldReset(false);
    }
  }, [isSuccessDeliverable, deliverableData]);

  useEffect(() => {
    if (getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
      // need to check update scenario
      reset({
        ...defaultValues,
        [CreateDeliverablesFormKeys.addDeliverableType]:
          getValues().addDeliverableType,
      });
      setSelectedUnit("");
    } else {
      resetClientExistingDeliverables();
    }
  }, [watch(CreateDeliverablesFormKeys.addDeliverableType)]);

  useEffect(() => {
    if (measurementShouldReset) {
      clearErrors([
        CreateDeliverablesFormKeys.measurementMinute,
        CreateDeliverablesFormKeys.measurementOther,
      ]);
      setValue(CreateDeliverablesFormKeys.measurementMinute, "");
      setValue(CreateDeliverablesFormKeys.measurementOther, null);
    }
    setMeasurementShouldReset(true);
  }, [watch(CreateDeliverablesFormKeys.units)]);

  const onFinalSubmitClick = () => {
    if (getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
      const {
        name,
        deliverableType,
        units,
        measurementMinute,
        measurementOther,
      } = getValues();

      const getMeasurement = () => {
        switch (units) {
          case UnitTypesKey.Minutes:
            return measurementMinute;
          case UnitTypesKey.Count:
          case UnitTypesKey.Days:
            return (measurementOther as number).toString();
          default:
            return "1";
        }
      };

      const params: CreateDeliverableParams = {
        deliverableType,
        units,
        measurements: getMeasurement(),
        localizedDeliverableNames: convertFormDataToLocalizeObj(name),
      };

      if (isEditDeliverable) {
        if (deliverableId) {
          updateDeliverableApi({ params, deliverableId });
        }
      } else {
        addNewDeliverableApi({ params, clientId });
      }
    } else {
      //existingKey
      const selectedDeliverablesIds: string[] = existingClientDeliverablesList
        .filter((row) => row.isSelected)
        .map((item) => item.id);

      if (isNotEmpty(selectedDeliverablesIds)) {
        const params = {
          deliverableIds: selectedDeliverablesIds,
        };
        addExistingDeliverableApi({
          params,
          clientId,
        });
      }
    }
  };

  const onConfirmClick = () => {
    handleSubmitForm(onFinalSubmitClick)();
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelMainModalHandler = () => {
    if (isDirtyForm) setShowCancel(true);
    else handleCloseModal();
  };

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  const deliverablesTitle = useMemo(() => {
    if (isEditDeliverable) {
      return replaceHashWithValue(
        fetchLabel(
          activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
          activitiesAndDeliverablesSiteCoreKeys.editDeliverableTitle,
        ),
        getValueOrBlank(deliverableData?.name),
      );
    } else {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.addDeliverable,
      );
    }
  }, [deliverableData]);

  const radioOptions = [
    {
      value: ADD_DELIVERABLE_TYPE.newKey,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.addNewDeliverable,
      ),
    },
    {
      value: ADD_DELIVERABLE_TYPE.existingKey,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.selectExistingDeliverables,
      ),
    },
  ];

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      CreateDeliverablesFormKeys.addDeliverableType,
      e.target.value as AddDeliverableType,
    );

    if (!isExistingDeliverableSuccess) {
      refetchExistingDeliverable({ frameworkType: DELIVERABLE_FRAMEWORK_TYPE.PROGRAM });
    }
  };

  const handleUnitChange = (value: string) => {
    setSelectedUnit(value);
  };

  const onHeaderCheckBoxChange = (checked: boolean) => {
    setIsAllSelected(checked);
    const updatedRows = existingClientDeliverablesList.map((row) => ({
      ...row,
      isSelected: checked,
    }));
    setExistingClientDeliverablesList(updatedRows);

    // Also update `allUpdatedRows` to reflect changes for the whole dataset
    const updatedAllRows = allUpdatedRows.map((row) => {
      // Find the matching row in `existingClientDeliverablesList` and update it
      const matchingRow = updatedRows.find(
        (updatedRow) => updatedRow.id === row.id,
      );
      return matchingRow ? { ...row, isSelected: matchingRow.isSelected } : row;
    });

    // Update `allUpdatedRows` state
    setAllUpdatedRows(updatedAllRows);
  };

  const handleRowCheckboxChange = (checked: boolean, rowId: string) => {
    // Update only the clicked row's `isSelected` value
    const updatedRows = existingClientDeliverablesList.map((row) =>
      row.id === rowId ? { ...row, isSelected: checked } : row,
    );
    setExistingClientDeliverablesList(updatedRows);

    // Check if all rows have isSelected as true
    const allSelected = updatedRows.every((row) => row.isSelected);
    setIsAllSelected(allSelected);

    const updatedAllRows = allUpdatedRows.map((row) =>
      row.id === rowId ? { ...row, isSelected: checked } : row,
    );
    setAllUpdatedRows(updatedAllRows);
  };

  const {
    control: existingFilterFormControl,
    watch: existingWatch,
    reset: resetExistingFilter,
    getValues: getExistingFilterValues,
  } = useForm<ExistingDeliverableFilterFormProps>({
    mode: "all",
    defaultValues: {
      [ExistingDeliverableFormKeys.deliverableType]: "",
      [ExistingDeliverableFormKeys.name]: "",
      [ExistingDeliverableFormKeys.units]: "",
    },
  });

  const applyFilters = (data: ExistingDeliverablesType[]) => {
    const filters = getExistingFilterValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue?.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        if (filterValue === "") return true;

        const itemValue = item[key]?.toString().toLowerCase();

        return itemValue?.includes(filterValueStr);
      });
    });
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filteredData = applyFilters(allUpdatedRows);
      setExistingClientDeliverablesList(filteredData);
      resetPagination();
    }
  }, [
    existingWatch(ExistingDeliverableFormKeys.name),
    existingWatch(ExistingDeliverableFormKeys.deliverableType),
    existingWatch(ExistingDeliverableFormKeys.units),
  ]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const activityHeaderText = useMemo(() => {
    if (isEditDeliverable) {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.editDeliverableNote,
      );
    } else {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
        activitiesAndDeliverablesSiteCoreKeys.createDeliverableNote,
      );
    }
  }, []);

  return (
    <MuiModal
      open={show}
      onClose={cancelMainModalHandler}
      title={deliverablesTitle}
      primaryButtonTitle={
        isEditDeliverable ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={cancelMainModalHandler}
      hasLargeContent
    >
      <CreateDeliverableWrapper data-testid={`${pageId}-add-deliverable`}>
        <InformationBadge message={activityHeaderText} />
        {!isEditDeliverable && (
          <MuiBox className="radio-container">
            <FormController
              control={control}
              name={CreateDeliverablesFormKeys.addDeliverableType}
            >
              <MuiRadioGroup
                label={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
                  activitiesAndDeliverablesSiteCoreKeys.selectType,
                )}
                value={
                  getValues(CreateDeliverablesFormKeys.addDeliverableType) ?? ""
                }
                onChange={handleSearchTypeChange}
                options={radioOptions}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
        )}

        {getValues().addDeliverableType === ADD_DELIVERABLE_TYPE.newKey && (
          <NewDeliverable
            pageId={pageId}
            control={control}
            selectedUnit={selectedUnit}
            manageLocaleTitle={deliverablesTitle}
            manageLocaleInfoMessage={activityHeaderText}
            handleSaveManageLocal={(manageLocals) =>
              setValue(CreateDeliverablesFormKeys.name, manageLocals)
            }
            handleUnitChange={handleUnitChange}
            userSelectedLanguage={userSelectedLanguage}
          />
        )}

        {getValues().addDeliverableType ===
          ADD_DELIVERABLE_TYPE.existingKey && (
          <CreateClientExistingDeliverables
            onHeaderCheckBoxChange={onHeaderCheckBoxChange}
            isAllSelected={isAllSelected}
            pageId={pageId}
            handleRowCheckboxChange={handleRowCheckboxChange}
            existingFilterFormControl={existingFilterFormControl}
            tableRef={tableRef}
            existingDeliverablesList={existingClientDeliverablesList}
            noDataInApi={allUpdatedRows.length === 0}
          />
        )}

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
          />
        )}
      </CreateDeliverableWrapper>
    </MuiModal>
  );
};
